import React from 'react';
import { MockService } from 'types/rest/mockServiceTypes';
import _ from 'lodash';
import ExpandLink from 'components/blazeTable/ExpandLink';
import { WithTranslation, withTranslation } from 'react-i18next';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import i18n from 'i18n';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { isUpgradeMockWarningDisplay } from '../../../../../utils/mockServiceUtils';
import AttentionIcon from '../../../../../components/alerts/AttentionIcon';

interface ServiceNameCellProps {
  mockService: MockService;
  onChange: (rowData: MockService, name: string) => void;
  toggleExpandDetail: (id: number) => void;
  isEditable: boolean;
  disabled: boolean;
  openMockServiceWarningModal: (rowData: MockService) => void;
}

class MockServiceNameCell extends React.Component<ServiceNameCellProps & WithTranslation> {
  state = {
    name: this.props.mockService.name,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<ServiceNameCellProps & WithTranslation>): void {
    if (nextProps.mockService.name !== this.state.name) {
      this.setState({ name: nextProps.mockService.name });
    }
  }

  nameChangedHandler = (name: string) => {
    this.setState({ name: name }, () => {
      this.triggerUpdate(name);
    });
  };

  triggerUpdate = _.debounce((name: string) => {
    this.props.onChange(this.props.mockService, name);
  }, 600);

  getDangerIcon = () => {
    const extension = this.props.mockService && isUpgradeMockWarningDisplay(this.props.mockService) && (
      <AttentionIcon title={''} onClick={() => this.props.openMockServiceWarningModal(this.props.mockService)} />
    );
    return extension;
  };

  render() {
    //option drop-down if adding new item
    if (this.props.isEditable) {
      return (
        <SeviceNameInputWithValidationHook
          id={this.props.mockService.id}
          name={this.state.name}
          disabled={this.props.disabled}
          serviceChangedHandler={this.nameChangedHandler}
        />
      );
    } else {
      const { isDraft, id, serviceMockId, name } = this.props.mockService;
      const displayedId = isDraft ? serviceMockId : id;
      const title = `${name} (ID: ${displayedId})`;

      return (
        <ExpandLink
          value={this.props.mockService.name}
          id={this.props.mockService.id}
          title={title}
          onToggleExpandDetail={() => {
            this.props.toggleExpandDetail(this.props.mockService.id);
          }}
          extension={this.getDangerIcon()}
        />
      );
    }
  }
}

export default withTranslation('mockServices')(MockServiceNameCell);

/**
 * Input validation hook. It is functional component wrapping BzFormControlWithHook which cannot be
 * used inside of the stateful component.
 * @constructor
 */
const SeviceNameInputWithValidationHook = (props: {
  name: string;
  disabled: boolean;
  id: number;
  serviceChangedHandler: any;
}) => {
  const seviceNameInput = useFormInput(
    props.name,
    (fn: CallbackFn) => {
      if (fn.isValid) {
        //Do update
        props.serviceChangedHandler(fn.value);
      }
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('mockServices:mockServicesTable.mockServiceNameRequired'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('mockServices:mockServicesTable.mockServiceIllegalChars'),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('mockServices:mockServicesTable.mockServiceMaximumLength'),
    },
  );

  return (
    <>
      <BzFormControlWithHook
        className={'serviceMockName-' + props.id}
        useFormInputHook={seviceNameInput}
        placeholder="Enter service name"
        required={true}
        value={props.name + ''}
        disabled={props.disabled}
      />
    </>
  );
};
